import type {
  KYCQuestionDefinition,
  KYCQuestionnaireElementLayout,
} from '@anchorage/graphql/generated';
import { KYCQuestionRuleType } from '@anchorage/graphql/generated';
import { handleError } from '@anchorage/sentry';
import type { CustomLinkProps } from '@anchorage/utils';
import { uuid } from '@anchorage/utils';
import { Button } from '@latitude/button';
import { Card } from '@latitude/card';
import { reactHookForm } from '@latitude/form';
import type { IconName } from '@latitude/icons';
import { Icon } from '@latitude/icons';
import { Text } from '@latitude/text';
import classNames from 'classnames';
import { useQuestionnaire } from 'hooks/useQuestionnaire/useQuestionnaire';
import type { ComponentType } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { QuestionNote } from '../QuestionNote';
import { GroupModal } from './components/GroupModal/GroupModal';
import {
  getGroupIconFromTemplate,
  getGroupItemNameFromTemplate,
  getMinMaxFromRules,
  groupHasSomeAnswer,
  shouldShowEditButton,
} from './helpers';

import css from './styles.module.css';

export const GroupIcon = (icon: string): IconName => {
  switch (icon) {
    case 'person':
    case 'INDIVIDUAL':
      return 'user';
    case 'RELATED_PARTY':
    case 'RELATED_PARTY_INSTITUTION':
      return 'building';
    default:
      handleError(new Error(`Case ${icon} not found for GroupIcon`));
      return 'user';
  }
};

type Props = {
  question: KYCQuestionDefinition;
  layout?: KYCQuestionnaireElementLayout;
  isDisabled?: boolean;
  answeredOptions?: Record<string, string>;
  CustomLink?: ComponentType<CustomLinkProps>;
  trackFn?: (questionId: string) => void;
  showTriggered?: boolean;
};

function Group({
  question,
  layout,
  isDisabled = false,
  answeredOptions,
  CustomLink,
  trackFn = () => {},
  showTriggered = false,
}: Props) {
  const { useFieldArray, useFormContext } = reactHookForm;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openedGroupIndex, setOpenedGroupIndex] = useState(0);
  const [openedGroupId, setOpenedGroupId] = useState('');

  const { getValues, control, getFieldState, trigger, watch } =
    useFormContext();
  const groupQuestions = question.group || [];
  const groupName = `${question.id}.group_answers`;

  // Extract minSize and maxSize from question.rules directly
  // This is needed for both Yup and non-Yup modes to control UI behavior
  const { min: minSize, max: maxSize } = getMinMaxFromRules(question.rules);

  const questionText = question.text;
  const questionHeader = question.header;

  const { fields, append, remove } = useFieldArray({
    name: groupName,
    control,
  });
  const numberOfGroups = fields.length;

  // Find all nested questions that have DISTINCT or MAX_SUM rules
  const questionsWithGroupRules = useMemo(() => {
    return question.group.filter((q) =>
      q.rules?.some(
        (rule) =>
          rule.type === KYCQuestionRuleType.DISTINCT ||
          rule.type === KYCQuestionRuleType.MAX_SUM,
      ),
    );
  }, [question.group]);

  useEffect(() => {
    if (questionsWithGroupRules.length === 0) {
      return;
    }
    // listen for changes in any of the questions that have DISTINCT or MAX_SUM rules
    const subscription = watch((_, { name }) => {
      if (name?.startsWith(groupName)) {
        // extract the question ID from the field name
        const id = name.split('.').slice(-2, -1)[0];
        const questionWitRule = questionsWithGroupRules.find(
          (q) => q.id === id,
        );
        // if the changed field is not one of the questions with rules, do nothing
        if (!questionWitRule) {
          return;
        }
        // trigger validation for all group entries for that question, so that
        // the errors show for all affected entries
        for (let index = 0; index < fields.length; index++) {
          const fieldPath = `${groupName}.${index}.${questionWitRule.id}.freeformText`;
          trigger(fieldPath);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [questionsWithGroupRules, groupName, watch, trigger, fields.length]);

  const { shouldShowElement } = useQuestionnaire();
  const shouldShow = useMemo(
    () =>
      shouldShowElement?.({
        triggerOptionID: question.triggerOptionID,
        answeredOptions,
      }),
    [question.triggerOptionID, shouldShowElement, answeredOptions],
  );

  /* add as many groups as the minimum required value */
  useEffect(() => {
    // hidden groups must not self-populate as that would cause rhf to try to validate them
    if (!shouldShow && !showTriggered) {
      return;
    }
    const diff = numberOfGroups - minSize;
    if (diff < 0) {
      append(Array.from({ length: Math.abs(diff) }, () => ({ id: uuid() })));
    }
  }, [numberOfGroups, minSize, append, fields, shouldShow, showTriggered]);

  const onAddDetailsClick = useCallback(
    ({ groupId, groupIndex }: { groupId: string; groupIndex: number }) => {
      setOpenedGroupIndex(groupIndex);
      setOpenedGroupId(groupId);
      setIsModalOpen(true);
    },
    [],
  );

  if (!shouldShow && !showTriggered) {
    return null;
  }

  const disableNewItems = !!layout?.groupTemplate?.newItemDisabled;

  const groupIconName = layout?.groupTemplate?.icon
    ? GroupIcon(layout.groupTemplate.icon)
    : null;

  return (
    <div className={css.groupsWrapper}>
      {questionText && (
        <div className={css.groupsTitle}>
          <Text variant="bodyMedium" color="strong">
            {questionText}
          </Text>
          {questionHeader ? (
            <Text
              className={css.subtext}
              variant="captionRegular"
              color="muted"
            >
              {questionHeader}
            </Text>
          ) : null}
          {question.optional && (
            <Text className={css.optional} variant="captionMedium">
              Optional
            </Text>
          )}
        </div>
      )}
      <div className={css.groupsContainer}>
        {fields.map(({ id: rowId }, groupIndex) => {
          const hasError = getFieldState(`${groupName}[${groupIndex}]`)?.error;
          const hasSomeAnswer = groupHasSomeAnswer({
            groupQuestions,
            groupName,
            groupIndex,
            getValues,
          });
          const { groupItemName, groupItemDescription } =
            getGroupItemNameFromTemplate({
              groupTemplate: layout?.groupTemplate,
              groupIndex,
              groupName,
              getValues,
            });
          const showEditButton = layout?.groupTemplate?.triggerOptionIdForModal
            ? shouldShowEditButton({
                triggerOptionIdForModal:
                  layout?.groupTemplate?.triggerOptionIdForModal,
                groupQuestions,
                groupName,
                groupIndex,
                getValues,
              })
            : true;

          const groupItemNameClasses = classNames(
            css.groupItemName,
            (hasSomeAnswer || !showEditButton) && css.groupItemNameFilled,
          );

          const groupItemClasses = classNames(
            css.groupContainer,
            hasError && css.groupItemError,
          );

          let groupIconNameFromTemplate: IconName | null = null;
          if (layout?.groupTemplate?.iconTemplate) {
            const iconNameFromTemplate = getGroupIconFromTemplate({
              iconTemplate: layout.groupTemplate.iconTemplate,
              groupIndex,
              groupName,
              getValues,
            });

            if (iconNameFromTemplate) {
              groupIconNameFromTemplate = GroupIcon(iconNameFromTemplate);
            }
          }

          const editButton = hasSomeAnswer ? (
            <Button
              htmlType="button"
              variant="strong"
              size="tiny"
              type="ghost"
              leadingElement={<Icon name="edit" title="Edit details" />}
              onClick={() => onAddDetailsClick({ groupId: rowId, groupIndex })}
            />
          ) : (
            <Button
              htmlType="button"
              variant="strong"
              size="tiny"
              type="outline"
              leadingElement={<Icon name="plus" />}
              onClick={() => onAddDetailsClick({ groupId: rowId, groupIndex })}
              className={css.addDetailsButton}
            >
              {layout?.groupTemplate?.addDetailsButtonTitle ||
                layout?.groupTemplate?.itemTitle ||
                ''}
            </Button>
          );

          return (
            <Card className={groupItemClasses} key={rowId}>
              <div
                className={groupItemNameClasses}
                data-question-id={question.id}
              >
                {groupIconName && !groupIconNameFromTemplate ? (
                  <Icon name={groupIconName} className={css.groupItemIcon} />
                ) : null}
                {groupIconNameFromTemplate ? (
                  <Icon
                    name={groupIconNameFromTemplate}
                    className={css.groupItemIcon}
                  />
                ) : null}
                <div className={css.groupItemNameWrapper}>
                  <Text className={css.groupItemNameText} variant="bodyBold">
                    {groupItemName}
                  </Text>
                  {groupItemDescription ? (
                    <Text
                      variant="captionRegular"
                      className={css.groupDescription}
                    >
                      {groupItemDescription}
                    </Text>
                  ) : null}
                  {hasError ? (
                    <div className={css.errorRow}>
                      <Icon
                        name="alert-circle"
                        className={classNames(
                          css.groupItemErrorIcon,
                          'text-color-ref-ui-status-danger fill-current',
                        )}
                      />
                      <Text
                        variant="captionMedium"
                        className={css.groupItemErrorName}
                      >
                        Add or correct the information
                      </Text>
                    </div>
                  ) : null}
                </div>
              </div>
              {showEditButton ? editButton : null}
              {numberOfGroups > minSize && !isDisabled && !disableNewItems && (
                <Button
                  htmlType="button"
                  variant="neutral"
                  size="tiny"
                  type="ghost"
                  onClick={() => remove(groupIndex)}
                  data-testid="remove-set-button"
                  rawProps={{ 'aria-label': 'Remove' }}
                  leadingElement={<Icon name="x-close" title="Remove set" />}
                />
              )}
            </Card>
          );
        })}
      </div>
      {(maxSize === undefined || maxSize > numberOfGroups) &&
        !isDisabled &&
        !disableNewItems && (
          <Button
            htmlType="button"
            variant="neutral"
            size="tiny"
            type="ghost"
            leadingElement={<Icon name="plus" />}
            onClick={() => {
              const emptyQuestions: Record<string, null> = {};
              groupQuestions.map((q) => (emptyQuestions[q.id] = null));
              append({ id: uuid(), ...emptyQuestions });
            }}
            className={css.groupMoreButton}
          >
            Add more
          </Button>
        )}
      {question.note && <QuestionNote note={question.note} />}
      <GroupModal
        title={layout?.groupTemplate?.itemTitle || ''}
        subtitle={layout?.groupTemplate?.itemSubtitle || ''}
        onModalClose={() => setIsModalOpen(false)}
        isModalOpen={isModalOpen}
        groupQuestions={groupQuestions}
        groupId={openedGroupId}
        groupNameAndIndex={`${groupName}.${openedGroupIndex}`}
        isGroupDisabled={isDisabled}
        answeredOptions={answeredOptions || {}}
        CustomLink={CustomLink}
        trackFn={trackFn}
      />
    </div>
  );
}

export { Group };
